import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useNotifications } from '../contexts/NotificationsContext';
import {
  Bell, BellOff, ChevronLeft, ChevronRight, ShoppingBag, PlusCircle, Car,
  MapPin, CheckCircle2, Star, XCircle, Gift, Settings, CreditCard,
  ClipboardList, Store, Building2
} from 'lucide-react';

// Mirrors NotificationType.icon + Notification.typeColor (KFCStKitts/Models/Notification.swift).
// SF Symbols → lucide-react; systemBlue/green/orange/red/purple/gray → hex.
const TYPE_META = {
  order_update:       { Icon: ShoppingBag,   color: '#007AFF' }, // bag.fill / blue
  order_status:       { Icon: ShoppingBag,   color: '#007AFF' }, // bag.fill / blue
  order_confirmed:    { Icon: ClipboardList, color: '#007AFF' }, // web legacy type
  new_order:          { Icon: PlusCircle,    color: '#007AFF' }, // plus.circle.fill / blue
  driver_assigned:    { Icon: Car,           color: '#34C759' }, // car.fill / green
  driver_accepted:    { Icon: Car,           color: '#34C759' }, // car.fill / green
  driver_picked_up:   { Icon: Car,           color: '#34C759' }, // car.fill / green
  driver_on_the_way:  { Icon: Car,           color: '#34C759' }, // car.fill / green
  driver_on_way:      { Icon: Car,           color: '#34C759' }, // web legacy type
  delivery_update:    { Icon: Car,           color: '#34C759' }, // car.fill / green
  driver_nearby:      { Icon: MapPin,        color: '#FF9500' }, // location.circle.fill / orange
  driver_arrived:     { Icon: MapPin,        color: '#FF9500' }, // location.circle.fill / orange
  order_ready:        { Icon: CheckCircle2,  color: '#FF9500' }, // checkmark.circle.fill / orange
  order_completed:    { Icon: Star,          color: '#34C759' }, // star.circle.fill / green
  delivery_completed: { Icon: Star,          color: '#34C759' }, // star.circle.fill / green
  order_cancelled:    { Icon: XCircle,       color: '#FF3B30' }, // xmark.circle.fill / red
  promotion:          { Icon: Gift,          color: '#AF52DE' }, // gift.fill / purple
  special_offer:      { Icon: Gift,          color: '#AF52DE' }, // web legacy type
  payment_confirmed:  { Icon: CreditCard,    color: '#34C759' }, // web legacy type
  restaurant_update:  { Icon: Store,         color: '#007AFF' }, // web legacy type
  system:             { Icon: Settings,      color: '#8E8E93' }, // gear / gray
};

export const notificationTypeMeta = (type) =>
  TYPE_META[type] || { Icon: Bell, color: '#8E8E93' };

// Admin-sent broadcasts fan out as type 'promotion' with data.broadcast='true'.
// They render with the CheF logo instead of a generic type icon (mirrors iOS
// Notification.isFromAdmin / NotificationTypeIcon).
export const isAdminBroadcast = (notification) =>
  notification?.type === 'promotion' || notification?.data?.broadcast === 'true';

// Circular Chef logo badge — drop-in replacement for the type-icon circle.
export function AdminBroadcastIcon({ size = 40 }) {
  return (
    <img
      src="/ChefLogo.jpg"
      alt="CheF SKN"
      className="rounded-full object-cover shrink-0 bg-white"
      style={{ width: size, height: size }}
    />
  );
}

// iOS `Text(date, style: .relative)` equivalent.
export const formatTimeAgo = (date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
};

// iOS `.formatted(date: .abbreviated, time: .shortened)` equivalent.
const formatDate = (date) =>
  new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

// Mirrors FirebaseManager.fetchRestaurant(byId:) used by the iOS detail view.
export function useRestaurantName(restaurantId) {
  const { db } = useFirebase();
  const [name, setName] = useState(null);

  useEffect(() => {
    if (!restaurantId) {
      setName(null);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, 'restaurants', restaurantId))
      .then((snap) => {
        if (!cancelled && snap.exists()) setName(snap.data().name || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [restaurantId, db]);

  return name;
}

// Mirrors CustomerNotificationDetailView body: 56pt tinted icon circle + title2 title +
// date, "Status Update" headline, message in a rounded box, driver arrival photo,
// green restaurant chip. Shared by the Notifications sheet and this standalone page.
export function NotificationDetailBody({ notification }) {
  const navigate = useNavigate();
  const meta = notificationTypeMeta(notification.type);
  const restaurantName = useRestaurantName(notification.restaurantId);
  const [showFullImage, setShowFullImage] = useState(false);
  // iOS attaches the doorstep photo to driver_arrived; the web driver also
  // carries it on delivery_completed when the photo came after arrival.
  const arrivalImageUrl =
    ['driver_arrived', 'delivery_completed'].includes(notification.type)
      ? notification.data?.arrivalImageUrl
      : null;

  return (
    <div className="space-y-5">
      {/* Header — HStack(spacing: 16); ChefLogo for admin broadcasts */}
      <div className="flex items-center gap-4">
        {isAdminBroadcast(notification) ? (
          <AdminBroadcastIcon size={56} />
        ) : (
          <span
            className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${meta.color}1A` }}
          >
            <meta.Icon size={24} style={{ color: meta.color }} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-[22px] font-bold text-gray-900 leading-tight">
            {notification.title}
          </h2>
          <p className="text-[15px] text-[#6D6D72] mt-0.5">
            {formatDate(notification.createdAt)}
          </p>
        </div>
      </div>

      {/* Status Update section — promo image hero on top, message below */}
      <div className="space-y-4">
        {/* Admin broadcast image — tap opens a full-screen viewer */}
        {notification.data?.imageURL && (
          <button
            type="button"
            onClick={() => setShowFullImage(true)}
            className="relative block w-full text-left"
          >
            <img
              src={notification.data.imageURL}
              alt=""
              className="w-full h-[220px] object-cover rounded-xl"
            />
            <span className="absolute bottom-2.5 right-2.5 bg-black/55 text-white text-[12px] font-semibold px-2.5 py-1 rounded-full">
              Tap to view
            </span>
          </button>
        )}

        <h3 className="text-[17px] font-semibold text-gray-900">Status Update</h3>

        <p className="text-[15px] leading-relaxed text-[#6D6D72] bg-white rounded-xl p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
          {notification.message}
        </p>

        {/* Driver arrival photo */}
        {arrivalImageUrl && (
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-gray-900">Driver Location Photo</p>
            <img
              src={arrivalImageUrl}
              alt="Driver location"
              className="w-full h-[200px] object-cover rounded-xl"
            />
          </div>
        )}

        {/* Restaurant chip — building.2 + green tint */}
        {notification.restaurantId && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#34C759]/10">
            <Building2 size={15} className="text-[#34C759] shrink-0" />
            {restaurantName ? (
              <span className="text-[15px] text-[#6D6D72]">Restaurant: {restaurantName}</span>
            ) : (
              <span className="text-[13px] text-[#6D6D72]">Restaurant: Loading...</span>
            )}
          </div>
        )}

        {/* Linked order row (web equivalent of tapping through to the order) */}
        {notification.orderId && (
          <button
            onClick={() => navigate(`/orders/${notification.orderId}`)}
            className="w-full flex items-center gap-3 bg-white rounded-xl px-4 py-3 min-h-[44px] text-left active:bg-gray-100 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
          >
            <span className="w-[30px] h-[30px] rounded-[7px] bg-[#FF3B30] flex items-center justify-center shrink-0">
              <ShoppingBag size={17} className="text-white" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[16px] text-gray-900 truncate">View Order</span>
              <span className="block text-[13px] text-[#6D6D72] truncate">
                Order #{notification.orderId.slice(0, 8)}
              </span>
            </span>
            <ChevronRight size={17} className="text-[#C7C7CC] shrink-0" />
          </button>
        )}
      </div>

      {/* Full-screen image viewer */}
      {showFullImage && notification.data?.imageURL && (
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setShowFullImage(false)}
        >
          <img
            src={notification.data.imageURL}
            alt=""
            className="max-w-full max-h-full object-contain"
          />
          <button
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center text-xl"
            aria-label="Close image"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// Standalone route (/notifications/:notificationId) — same content as the iOS sheet,
// wrapped in an inline-title nav bar with a back chevron ("Done" equivalent).
function NotificationDetailPage() {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const { notifications, loading, markAsRead } = useNotifications();
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (notifications.length > 0) {
      const foundNotification = notifications.find(n => n.id === notificationId);
      if (foundNotification) {
        setNotification(foundNotification);
        // Mark as read when viewing (mirrors iOS onAppear)
        if (!foundNotification.isRead) {
          markAsRead(notificationId);
        }
      }
    }
  }, [notificationId, notifications, markAsRead]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] flex flex-col items-center justify-center px-6 space-y-4">
        <BellOff size={48} className="text-[#8E8E93]" strokeWidth={1.5} />
        <h2 className="text-[17px] font-semibold text-gray-900">Notification Not Found</h2>
        <p className="text-[15px] text-[#6D6D72] text-center">
          The notification you're looking for doesn't exist or has been deleted.
        </p>
        <button
          onClick={() => navigate('/dashboard/notifications')}
          className="bg-[#FF3B30] text-white text-[15px] font-semibold px-6 py-2 rounded-full active:opacity-80"
        >
          Back to Notifications
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar — back chevron + centered "Notification" title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-3 h-11 max-w-lg mx-auto">
          <button
            onClick={() => navigate('/dashboard/notifications')}
            className="flex items-center text-[#FF3B30] shrink-0 min-w-[36px]"
            aria-label="Back to notifications"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold text-gray-900">
            Notification
          </h1>
          <div className="min-w-[36px]" />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5">
        <NotificationDetailBody notification={notification} />
      </div>
    </div>
  );
}

export default NotificationDetailPage;
